/**
 * The world panel — the codex, ported as-is.
 *
 * Everything the story has accumulated that is not the character: the places it knows, the
 * factions it has an opinion about, the people it has met, and the facts it has established.
 * All of it comes from `GET /stories/{id}/state`'s `world` (P1 keeps it off the `chapter`
 * event precisely because it is modal-only content nobody is looking at during a turn).
 *
 * **This file is the sections; it is not the cards and it is not the present tense.** The
 * shapes live in `cards.tsx` and the pinned block in `HereNow.tsx`, and the split is what lets
 * an NPC be drawn identically whether they are standing in front of you or filed twelve rows
 * down. What stays here is the drawer, the shortcut, and the two key→name joins the sections
 * need: NPCs carry a location key and places carry faction keys, and the arrays beside them
 * are the only things that know the names.
 *
 * The rest of `TODO.md` §4 is still deferred — tabs, a filter box, cross-links, and eventually
 * an actual node-graph map over `LocationState.connections`. What is here is the same surface
 * as before with the character panel's shapes: collapsed sections instead of one long scroll
 * of cards, a disclosure per entity instead of a hover tooltip, and the enum vocabulary in one
 * exhaustive table (`world.ts`) instead of inline ternaries.
 *
 * **`w` opens it, and a visible control does too.** A shortcut nobody can discover
 * is a shortcut for the person who wrote it; the world codex's checkbox asks for both, and the guard on
 * the key is what makes the shortcut survivable — the action box is one keystroke away at all
 * times and a `w` that swallowed the first letter of *"walk north"* would be worse than no
 * shortcut at all.
 *
 * **The open state moved out of this component** (U6, `codex.ts`). A mention in the middle of
 * a chapter opens this panel at a card, and the two have no path to each other; what stays
 * here is the drawer, the shortcut and the two key→name joins the sections need.
 */
import { useEffect } from 'react';
import type { LocationRef, WorldSnapshot } from '@/api/generated';
import { Faction, Npc, Place } from '@/features/world/cards';
import { HereNow } from '@/features/world/HereNow';
import { useCodexStore, type CodexTarget } from '@/features/world/codex';
import { codexElementId } from '@/features/world/codexAnchor';
import { useT } from '@/i18n/context';
import { Dialog } from '@/ui/Dialog';
import { Nothing, Section } from '@/ui/Section';
import { isTyping } from '@/ui/typing';

interface WorldPanelProps {
  /** Null while `GET /stories/{id}/state` is in flight. The toggle exists regardless. */
  world: WorldSnapshot | null;
  /**
   * Where the character is standing — `character.location`, from the other half of the same
   * payload. The snapshot itself does not say which of its locations is the current one, and
   * "here, now" is the whole reason the panel needs to know.
   */
  here?: LocationRef | null;
  /** Why there is no world, when the request failed rather than is pending. */
  note?: string | null;
  /**
   * Refetch the story state, called every time the panel opens.
   *
   * **U6's one non-obvious bug lives here.** `src/turn/commit.ts` marks `world` stale with
   * `refetchType: 'none'` — the commit patches caches and never invalidates them — so the NPC
   * the DM *just* introduced, which is the most interesting mention on the page, is not in the
   * cached snapshot when the chapter renders. Refetching on open is the fix, and it also
   * settles the pre-existing "the codex is one turn behind" behaviour. Same shape as
   * `Journal`'s `onOpen`, which goes back for a summary the client never learns has landed.
   */
  onOpen?: () => void;
}

export function WorldPanel({ world, here = null, note = null, onOpen }: WorldPanelProps) {
  const t = useT();
  const open = useCodexStore((state) => state.isOpen);
  const target = useCodexStore((state) => state.target);
  const opens = useCodexStore((state) => state.opens);
  const toggle = useCodexStore((state) => state.toggle);
  const close = useCodexStore((state) => state.close);

  // Keyed on `opens`, not on `target`: tapping the same name twice has to work, and the
  // second tap changes no value at all (`codex.ts`).
  useEffect(() => {
    if (opens > 0) onOpen?.();
  }, [opens, onOpen]);

  /**
   * The shortcut, with the one guard that matters: **not while the player is typing.**
   *
   * The action box has no modifier and no focus discipline around it — it is a plain text
   * input sitting at the foot of every screen this panel appears on — so an unguarded `w`
   * would eat a letter out of every action beginning with one. `ui/typing.ts` is the shared
   * guard and argues itself; the choice cards' `1`–`6` is the other caller. Escape already
   * closes the dialog, so this handler only ever has to open.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'w' && event.key !== 'W') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTyping(event.target)) return;
      toggle();
    };

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [toggle]);

  return (
    <>
      <button
        type="button"
        aria-expanded={open}
        aria-keyshortcuts="w"
        onClick={toggle}
        className="border-border bg-bg/90 text-fg-muted hover:border-accent hover:text-accent z-20 border px-3 py-1.5 font-mono backdrop-blur transition-colors"
        style={{ fontSize: 'var(--text-meta)' }}
      >
        {t('ui.nav.world')}
      </button>

      <Dialog open={open} onClose={close} title={t('ui.nav.world')} variant="drawer" side="right">
        {world === null ? (
          <Nothing>{note ?? t('ui.panel.reading_state')}</Nothing>
        ) : (
          <WorldBody world={world} here={here} target={target} opens={opens} />
        )}
      </Dialog>
    </>
  );
}

function WorldBody({
  world,
  here,
  target,
  opens,
}: {
  world: WorldSnapshot;
  here: LocationRef | null;
  target: CodexTarget | null;
  opens: number;
}) {
  const t = useT();

  /**
   * Scroll the targeted card into view and put the reader on it.
   *
   * After a paint, because the section and the card both have to have *opened* first — they
   * do that in their own effects, and a scroll measured against a collapsed `<details>` lands
   * somewhere else. `Dialog` takes focus for itself on mount, so this runs after that too.
   *
   * A key that is genuinely gone — `merge_npc` folds two rows into one — simply finds no
   * element, and the panel stays open at the top rather than erroring. The server validated
   * the key when the chapter was written; it cannot promise the card still exists today.
   */
  useEffect(() => {
    if (target === null) return;
    const frame = requestAnimationFrame(() => {
      const card = document.getElementById(codexElementId(target.kind, target.key));
      card?.scrollIntoView({ block: 'center' });
      card?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [target, opens]);

  const isTarget = (kind: CodexTarget['kind'], key: string): boolean =>
    target !== null && target.kind === kind && target.key === key;
  // NPCs carry a location *key* and places carry faction *keys*; the arrays beside them are
  // the only things that know the names. Joins across two halves of one payload, not
  // derivations — the server sent both. Both fall back to the key, which is what the panel
  // showed everywhere before L13 and is still the only honest answer for a faction a place
  // names but the player has never met.
  const placeNames = new Map(world.locations.map((place) => [place.name, place.display_name]));
  const factionNames = new Map(
    world.factions.map((faction) => [faction.name, faction.display_name]),
  );

  return (
    <>
      <HereNow world={world} here={here} />

      <Section
        title={t('ui.world.places')}
        count={world.locations.length}
        reveal={target?.kind === 'location'}
      >
        {world.locations.length === 0 ? (
          <Nothing>{t('ui.world.no_places')}</Nothing>
        ) : (
          <ul className="flex flex-col">
            {world.locations.map((place) => (
              <li key={place.name}>
                <Place
                  place={place}
                  factionNames={factionNames}
                  targeted={isTarget('location', place.name)}
                />
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title={t('ui.world.factions')}
        count={world.factions.length}
        reveal={target?.kind === 'faction'}
      >
        {world.factions.length === 0 ? (
          <Nothing>{t('ui.world.no_factions')}</Nothing>
        ) : (
          <ul className="flex flex-col">
            {world.factions.map((faction) => (
              <li key={faction.name}>
                <Faction faction={faction} targeted={isTarget('faction', faction.name)} />
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title={t('ui.world.people')}
        count={world.npcs.length}
        reveal={target?.kind === 'npc'}
      >
        {world.npcs.length === 0 ? (
          <Nothing>{t('ui.world.no_people')}</Nothing>
        ) : (
          <ul className="flex flex-col">
            {world.npcs.map((npc) => (
              <li key={npc.name}>
                <Npc
                  npc={npc}
                  placeNames={placeNames}
                  // The anchor is minted here and not in `HereNow`, which draws the same
                  // people a second time (`codexAnchor.ts`).
                  id={codexElementId('npc', npc.name)}
                  targeted={isTarget('npc', npc.name)}
                />
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title={t('ui.world.facts')} count={world.permanent_facts.length}>
        {world.permanent_facts.length === 0 ? (
          <Nothing>{t('ui.world.no_facts')}</Nothing>
        ) : (
          <ul className="flex flex-col gap-2">
            {world.permanent_facts.map((fact) => (
              <li key={fact} className="text-fg-muted" style={{ fontSize: 'var(--text-meta)' }}>
                {fact}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/*
        Its own section rather than a badge inside the one above, because the two are read as
        different kinds of thing: what is settled, and what the story still owes an answer to.
        Italic is the whole of the visual distinction and it is enough — a rumour is a fact
        someone said, and it is drawn like reported speech.
      */}
      <Section title={t('ui.world.rumours')} count={world.rumours.length}>
        {world.rumours.length === 0 ? (
          <Nothing>{t('ui.world.no_rumours')}</Nothing>
        ) : (
          <ul className="flex flex-col gap-2">
            {world.rumours.map((rumour) => (
              <li
                key={rumour}
                className="text-fg-muted italic"
                style={{ fontSize: 'var(--text-meta)' }}
              >
                {rumour}
              </li>
            ))}
          </ul>
        )}
      </Section>
    </>
  );
}
